import { Box, CloseButton, Grid, GridItem } from '@chakra-ui/react';
import React, { Suspense, useContext, useMemo, useState } from 'react';
import { commonStyle } from '../../../../common/styles';
import { DialogStateContext } from '../../../../context/DialogStateContextProvider';
import { LanguageContext } from '../../../../context/LanguageContextProvider';
import HelpButton, { HelpButtonNameType } from './HelpButton';

const NOTE_MESSAGE =
  '当サービスは運行中の全車両の位置や運行状況を確認することができます。\nただし現在の運行状況を完全に保証するものではありません。\n機器の不具合や通信の遅延等により、実際の運行状況と異なる場合があります。\n表示される運行情報は目安としてご理解ください。\nメンテナンス等のために予告なく配信を中断・中止する場合があります。';
const NOTE_MESSAGE_EN =
  'This service allows you to check the location and operation status of all vehicles in operation.\nHowever, it does not completely guarantee the current operation status.\nIt may differ from the actual operation status due to equipment malfunctions, communication delays, etc.\n  Please understand that the displayed operation information is only a guideline.\n  Distribution may be interrupted or canceled without notice for maintenance or other reasons.';

const BUTTON_AREAS = [
  'brief',
  'map',
  'button',
  'info',
  'how-to',
  'others'
] as const;
const BUTTONS: Array<HelpButtonNameType> = [
  'かんたん説明',
  'マップ',
  'ボタン',
  '運行情報',
  '電車の乗り方',
  'その他'
];
const BUTTONS_EN: Array<HelpButtonNameType> = [
  'Brief Description',
  'Map',
  'Button',
  'Streetcar Information',
  'How to ride',
  'Others'
];

const HelpDialog: React.FC = () => {
  const { language } = useContext(LanguageContext);
  const { closeDialog } = useContext(DialogStateContext);
  const [selectedButton, setSelectedButton] = useState<number>(0);

  // ヘルプ画像は容量が大きいため、動的にロードする
  // useMemoしないと運行情報の更新のたびにスクロール位置がリセットされた
  const HelpImages = useMemo(
    () =>
      React.lazy(() => {
        const type: typeof BUTTON_AREAS[number] = BUTTON_AREAS[selectedButton];

        if (language === 'en') {
          switch (type) {
            case 'brief':
              return import('../../../atoms/svg/help/BriefEn');
            case 'map':
              return import('../../../atoms/svg/help/MapEn');
            case 'button':
              return import('../../../atoms/svg/help/ButtonEn');
            case 'info':
              return import('../../../atoms/svg/help/InfoEn');
            case 'how-to':
              return import('./contents/HowToRide');
            case 'others':
              return import('./contents/Others');

            default:
              return import('../../../atoms/svg/help/BriefEn');
          }
        } else {
          switch (type) {
            case 'brief':
              return import('../../../atoms/svg/help/Brief');
            case 'map':
              return import('../../../atoms/svg/help/Map');
            case 'button':
              return import('../../../atoms/svg/help/Button');
            case 'info':
              return import('../../../atoms/svg/help/Info');
            case 'how-to':
              return import('./contents/HowToRide');
            case 'others':
              return import('./contents/Others');

            default:
              return import('../../../atoms/svg/help/Brief');
          }
        }
      }),
    [selectedButton, language]
  );

  return (
    <Grid
      w='100%'
      h='100%'
      gridTemplateAreas={`'...' 'header' '...' 'note' '...' 'buttons' '...' 'content' '...'`}
      gridTemplateRows={`5px 50px 5px auto 10px 85px 3px 1fr 0`}
      gridTemplateColumns={'100%'}
      overflowY='auto'
    >
      {/* ヘッダー */}
      <GridItem gridArea='header'>
        <Grid
          w='100%'
          h='100%'
          gridTemplateAreas={`'title1 close' 'title2 close'`}
          gridTemplateColumns={`1fr 50px`}
          gridTemplateRows={`1fr 1fr`}
          fontWeight='bold'
        >
          {/* タイトル */}
          <GridItem
            gridArea='title1'
            fontSize='16px'
            color='white'
            sx={{
              ...commonStyle.center,
              alignItems: 'flex-end'
            }}
          >
            {language === 'en' ? 'Nagasaki Electric Tramway' : '長崎電気軌道'}
          </GridItem>

          <GridItem
            gridArea='title2'
            fontSize='16px'
            color='white'
            sx={{
              ...commonStyle.center,
              alignItems: 'flex-start'
            }}
          >
            {language === 'en'
              ? 'Service Information Help'
              : '運行情報サービスの使い方'}
          </GridItem>

          {/* 閉じるボタン */}
          <GridItem
            gridArea='close'
            sx={{
              ...commonStyle.center
            }}
          >
            <CloseButton
              color='white'
              bgColor='grey'
              w='35px'
              h='35px'
              size='md'
              style={{ boxShadow: 'none' }}
              onClick={closeDialog}
            ></CloseButton>
          </GridItem>
        </Grid>
      </GridItem>

      {/* 注意書き */}
      <GridItem
        gridArea='note'
        padding='0 6px'
        fontSize='10px'
        fontWeight='bold'
      >
        <Grid
          w='100%'
          h='100%'
          bgColor='white'
          borderRadius='5px'
          padding='5px 10px'
          whiteSpace={language === 'ja' ? 'pre-wrap' : undefined}
        >
          {language === 'en' ? NOTE_MESSAGE_EN : NOTE_MESSAGE}
        </Grid>
      </GridItem>

      {/* ボタン */}
      <GridItem
        gridArea='buttons'
        padding='0 5px'
        color='balck'
        fontSize='16px'
        sx={{
          ...commonStyle.center,
          justifyContent: 'flex-end'
        }}
      >
        <Grid
          w='100%'
          h='100%'
          gridTemplateAreas={`'brief map button' '... ... ...' 'info how-to others'`}
          gridTemplateRows={`1fr 5px 1fr`}
          gridTemplateColumns={`1fr 1fr 1fr`}
        >
          {(language === 'en' ? BUTTONS_EN : BUTTONS).map((name, index) => (
            <GridItem
              key={`help_button_${name}`}
              gridArea={BUTTON_AREAS[index]}
              fontWeight='bold'
              sx={{ ...commonStyle.center }}
            >
              <HelpButton
                name={name}
                selected={index === selectedButton}
                onClick={() => {
                  setSelectedButton(index);
                }}
              ></HelpButton>
            </GridItem>
          ))}
        </Grid>
      </GridItem>

      {/* 内容 */}
      <GridItem
        gridArea='content'
        padding='0 3px'
        color='black'
        overflow='auto'
        w='100%'
      >
        <Suspense
          fallback={
            <Box
              w='100%'
              h='100%'
              color='white'
              fontSize='20px'
              fontWeight='bold'
              sx={{ ...commonStyle.center }}
            >
              {'Loading...'}
            </Box>
          }
        >
          <Box w='100%' h='auto' bgColor='black' color='black'>
            <HelpImages></HelpImages>
          </Box>
        </Suspense>
      </GridItem>
    </Grid>
  );
};

export default React.memo(HelpDialog);
